import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { apiGet, apiPost } from "../services/api";
import NotificationPopup from "./NotificationPopup";
import Footer from "./Footer";
import { ArrowLeft, Save, Loader, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function EditStudentDetails({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [blankFields, setBlankFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  // Field labels mapping
  const fieldLabels = {
    Cname: "Name",
    FatherName: "Father Name",
    MotherName: "Mother Name",
    CAddress: "Address",
    CDob: "Date of Birth",
    gender: "Gender",
    CAadhar: "Aadhar Number",
    Fmobile: "Father Mobile Number",
    mobile: "Studen Mobile Number",
    email: "Email",
    department: "Department",
    school: "School",
    ProgrammeName: "Program Name",
    MMobile:"Mother Mobile Number"
  };

  const fieldTypes = {
    gender: "select",
    CDob: "date",
    CAddress: "textarea",
  };

  const fieldOptions = {
    gender: ["Male", "Female", "Other"],
  };

  // Fetch blank fields on component mount
  useEffect(() => {
    fetchBlankFields();
  }, []);

  const fetchBlankFields = async () => {
    try {
      setLoading(true);
      setError("");
      
      const res = await apiGet("getBlankFields");
      
      if (res && res.success) {
        if (res.blank_fields && res.blank_fields.length > 0) {
          // Convert field names to field objects with labels and types
          const fields = res.blank_fields.map((fieldName) => ({
            name: fieldName,
            label: fieldLabels[fieldName] || fieldName,
            type: fieldTypes[fieldName] || "text",
            options: fieldOptions[fieldName] || null,
          }));
          
          setBlankFields(fields);
          
          // Initialize form data with empty values
          const initialData = {};
          fields.forEach((field) => {
            initialData[field.name] = "";
          });
          setFormData(initialData);
        } else {
          setBlankFields([]);
          setNotification({
            type: "info",
            message: "All your details are complete! No updates needed.",
          });
        }
      } else {
        throw new Error(res?.message || "Failed to fetch blank fields");
      }
    } catch (err) {
      const errorMsg = err.message || "Unable to fetch blank fields. Please try again.";
      setError(errorMsg);
      setNotification({
        type: "error",
        message: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that all required fields are filled
    const emptyFields = blankFields.filter((field) => {
      const value = formData[field.name];
      return !value || (typeof value === "string" && value.trim() === "");
    });
    
    if (emptyFields.length > 0) {
      setNotification({
        type: "error",
        message: `Please fill all required fields: ${emptyFields.map((f) => f.label).join(", ")}`,
      });
      return;
    }

    try {
      setSubmitting(true);
      const res = await apiPost("updateStudentDetails", formData);

      if (res && res.success) {
        setNotification({
          type: "success",
          message: "Student details updated successfully!",
        });
        // Redirect to profile after successful submission
        setTimeout(() => {
          navigate("/profile");
        }, 1500);
      } else {
        throw new Error(res?.message || "Failed to update details");
      }
    } catch (err) {
      setNotification({
        type: "error",
        message: err.message || "Error updating details. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {notification && (
        <NotificationPopup
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={onLogout}
      />

      <div className="flex-1 flex flex-col">
        <Topbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
            {/* Header */}
            <div className="mb-6 flex items-center gap-4">
              <button
                onClick={() => navigate("/profile")}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                title="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-3xl font-bold text-gray-800">Edit Student Details</h1>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="bg-white rounded-lg shadow p-8 flex items-center justify-center gap-3">
                <Loader className="w-5 h-5 animate-spin text-blue-600" />
                <p className="text-gray-600">Loading blank fields...</p>
              </div>
            ) : blankFields.length === 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <p className="text-blue-800">
                  All your details are complete. No updates needed at this time.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
                  <p className="text-amber-900 font-semibold">
                    Fill all the below details to book the room
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 md:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {blankFields.map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field.label}
                        <span className="text-red-500 ml-1">*</span>
                      </label>

                      {field.type === "select" ? (
                        <select
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                          required
                        >
                          <option value="">Select {field.label}</option>
                          {field.options &&
                            field.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                        </select>
                      ) : field.type === "date" ? (
                        <input
                          type="date"
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                          required
                        />
                      ) : field.type === "textarea" ? (
                        <textarea
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                          required
                        />
                      ) : (
                        <input
                          type="text"
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                          required
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 justify-end">
                  <button
                    type="button"
                    onClick={() => navigate("/profile")}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
              </>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
